<template>
  <div class="numeric-grid-card">

    <!-- ── Stem: section eyebrow (falls back to subtitle), title, one
         optional line, optional image. Mirrors MatrixCard, which is the
         other card that asks one question about several rows. ── -->
    <div class="section-header">
      <p v-if="sectionLabel" class="section-eyebrow">{{ sectionLabel }}</p>
      <p v-else-if="subtitle" class="section-eyebrow">{{ subtitle }}</p>
      <h1 class="section-title">{{ title }}</h1>
      <p v-if="prompt" class="section-prompt">{{ prompt }}</p>
      <p class="section-progress-text">
        {{ filledCount }} {{ dict.of }} {{ rows.length }} {{ dict.filled }}
      </p>
    </div>

    <div v-if="imageId != null" class="stem-image-slot">
      <img
        v-if="imageRecord"
        class="stem-image"
        :src="imageUrl(imageRecord)"
        :alt="altTextFor(imageRecord, locale)"
        @error="onImgError($event, imageRecord, locale)"
      >
      <div v-else class="stem-image-placeholder">
        <span class="stem-image-icon" aria-hidden="true">🖼</span>
        <span class="stem-image-text">{{ dict.imagePlaceholder }} #{{ imageId }}</span>
      </div>
    </div>

    <p v-if="rows.length === 0" class="config-warning">{{ dict.noRows }}</p>

    <!-- ── The grid: one labelled number field per row. Each row carries
         its own unit and precision, so height (cm) and weight (kg) can
         share a card. ── -->
    <div class="row-list">
      <div v-for="row in rows" :key="row.id" class="grid-row">
        <div class="grid-row-header">
          <label class="grid-row-label" :for="`${uid}-${row.id}`">{{ row.displayLabel }}</label>
          <button
            v-if="row.hasTooltip"
            type="button"
            class="tooltip-button-small"
            :aria-expanded="String(!!tooltipsOpen[row.id])"
            :aria-controls="`ngt-${uid}-${row.id}`"
            @click="toggleTooltip(row.id)"
          >ⓘ</button>
        </div>
        <div
          v-if="row.hasTooltip && tooltipsOpen[row.id]"
          :id="`ngt-${uid}-${row.id}`"
          class="tooltip-content"
        >{{ row.tooltipText }}</div>

        <div class="number-field" :class="{ 'is-invalid': showErrorFor(row) }">
          <input
            :id="`${uid}-${row.id}`"
            type="text"
            class="number-input"
            :inputmode="row.cfg.decimals > 0 ? 'decimal' : 'numeric'"
            :value="raw[row.id] || ''"
            :placeholder="placeholderFor(row)"
            :aria-invalid="showErrorFor(row) ? 'true' : 'false'"
            :aria-describedby="showErrorFor(row) ? `nge-${uid}-${row.id}` : undefined"
            autocomplete="off"
            @input="onInput(row, $event)"
            @blur="touched = { ...touched, [row.id]: true }"
          >
          <span v-if="row.unitText" class="number-unit">{{ row.unitText }}</span>
        </div>

        <div class="input-footer">
          <p
            v-if="showErrorFor(row)"
            :id="`nge-${uid}-${row.id}`"
            class="input-error"
            role="alert"
          >{{ errorMessageFor(row) }}</p>
          <span v-else-if="row.rangeHint" class="input-hint">{{ row.rangeHint }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
// NumericGridCard — runtime card for question_type = 'numeric_grid'.
//
// Several number fields answered on one card. Built for the questions that
// are really one question repeated over a handful of things: household
// spend split across groceries / delivery / eating out / drinks, snacking
// frequency on weekdays versus the weekend, height together with weight.
// Authoring those as four separate `number` questions works but reads as
// four screens of near-identical chrome.
//
// Engine contract (docs/CONTRACT_CARD_ENGINE.md):
//   props : item, state
//   emits : state-changed   (no request-advance — typed fields must never
//                            auto-advance out from under the user, same
//                            rule as NumberCard)
//
// State shape: { values: { [row_id]: <number> | null } }. Emitted on every
// keystroke with the PARSED number per row, or null where the field is
// empty or not yet a number. Rows the participant never touched are simply
// absent, which is what keeps a half-filled grid honest in the export.
//
// Content shape (item.item.content):
//   title / subtitle / prompt : { de,fr,it,en }
//   image_id                  : number | null
//   input: { required }         card-level: all rows needed to satisfy a gate
//   rows: [ { id, label, tooltip_body,
//             input: { min, max, decimals, unit } } ]
//
// Validity comes from the shared helpers so this card, the engine's
// _isAnswered(), and a standalone `number` question cannot drift apart.

import i18n from '@/i18n';
import { imageUrl, altTextFor, onImgError } from '@/services/imageHelpers';
import { loadImageMap } from '@/services/imageApi';
import { formatSectionLabel } from '../sectionLabel.js';
import { evaluateNumber } from './numberValidation.js';
import { gridRows } from './numericGridValidation.js';

const DICT = {
  en: { imagePlaceholder: 'Image', of: 'of', filled: 'filled in', noRows: 'No rows are configured for this question.', errNumber: 'Enter a number.', errDecimals: 'Too many decimal places.', errMin: 'Must be at least', errMax: 'Must be at most', range: 'Enter a value', between: 'between', and: 'and', upTo: 'up to', from: 'from', decimalsNote: 'decimal places' },
  de: { imagePlaceholder: 'Bild', of: 'von', filled: 'ausgefüllt', noRows: 'Für diese Frage sind noch keine Zeilen konfiguriert.', errNumber: 'Bitte eine Zahl eingeben.', errDecimals: 'Zu viele Nachkommastellen.', errMin: 'Mindestens', errMax: 'Höchstens', range: 'Wert eingeben', between: 'zwischen', and: 'und', upTo: 'bis', from: 'ab', decimalsNote: 'Nachkommastellen' },
  fr: { imagePlaceholder: 'Image', of: 'sur', filled: 'remplis', noRows: 'Aucune ligne n\'est configurée pour cette question.', errNumber: 'Saisis un nombre.', errDecimals: 'Trop de décimales.', errMin: 'Au moins', errMax: 'Au plus', range: 'Saisir une valeur', between: 'entre', and: 'et', upTo: "jusqu'à", from: 'à partir de', decimalsNote: 'décimales' },
  it: { imagePlaceholder: 'Immagine', of: 'di', filled: 'compilati', noRows: 'Nessuna riga è configurata per questa domanda.', errNumber: 'Inserisci un numero.', errDecimals: 'Troppi decimali.', errMin: 'Almeno', errMax: 'Al massimo', range: 'Inserisci un valore', between: 'tra', and: 'e', upTo: 'fino a', from: 'da', decimalsNote: 'decimali' },
};

let cardInstance = 0;

export default {
  name: 'NumericGridCard',
  props: {
    item: { type: Object, required: true },
    state: { type: [Object, null], default: null },
  },
  emits: ['state-changed'],
  data() {
    cardInstance += 1;
    return {
      uid: `ng-${cardInstance}`,
      // Raw per-row text, kept locally for the same reason NumberCard keeps
      // it: a parsed number cannot reconstruct in-progress input ("12," or
      // "1'2"). Seeded from any prior state so returning to the card by
      // pressing Back shows what was typed.
      raw: this._initialRaw(),
      touched: {},
      tooltipsOpen: {},
      imageMap: null,
    };
  },
  created() {
    if (this.imageId != null) {
      loadImageMap()
        .then((map) => { this.imageMap = map; })
        .catch(() => { /* leave the placeholder if the library can't load */ });
    }
  },
  computed: {
    content() {
      return (this.item && this.item.item && this.item.item.content) || {};
    },
    locale() {
      return i18n.global.locale.value;
    },
    dict() {
      return DICT[this.locale] || DICT.en;
    },
    title() { return this._localised(this.content.title); },
    subtitle() { return this._localised(this.content.subtitle); },
    prompt() { return this._localised(this.content.prompt); },
    sectionLabel() { return formatSectionLabel(this.item, this.locale); },
    imageId() {
      const v = Number(this.content.image_id);
      return Number.isFinite(v) ? v : null;
    },
    imageRecord() {
      if (this.imageId == null || !this.imageMap) return null;
      return this.imageMap.get(this.imageId) || null;
    },
    /**
     * Rows decorated with everything the template needs. Built on top of
     * gridRows() so the config reading stays in one module; this layer only
     * adds localisation and the hint/error strings.
     */
    rows() {
      return gridRows(this.item).map((row) => {
        const tip = this._localised(row.tooltip_body);
        return {
          ...row,
          displayLabel: this._localised(row.label),
          tooltipText: tip,
          hasTooltip: !!tip,
          unitText: this._localised(row.cfg.unit),
          rangeHint: this._rangeHint(row),
        };
      });
    },
    filledCount() {
      return this.rows.filter(r => evaluateNumber(this.raw[r.id] || '', r.cfg).answered).length;
    },
  },
  watch: {
    // A row tipped in one language may have no tooltip in the next; close
    // orphans rather than render an empty popover. Same guard as MatrixCard.
    locale() {
      const valid = new Set(this.rows.filter(r => r.hasTooltip).map(r => r.id));
      const next = {};
      for (const k of Object.keys(this.tooltipsOpen)) {
        if (valid.has(k) && this.tooltipsOpen[k]) next[k] = true;
      }
      this.tooltipsOpen = next;
    },
  },
  methods: {
    _initialRaw() {
      const values = (this.state && this.state.values) || {};
      const out = {};
      for (const [rowId, v] of Object.entries(values)) {
        if (typeof v === 'number' && Number.isFinite(v)) out[rowId] = String(v);
      }
      return out;
    },
    _localised(field) {
      if (!field || typeof field !== 'object') return '';
      const order = [this.locale, 'en', 'de', 'fr', 'it'];
      for (const loc of order) {
        const v = field[loc];
        if (typeof v === 'string' && v.trim()) return v.trim();
      }
      return '';
    },
    _fmt(n) {
      // Locale decimal separator, so a hint reads 12,5 in de/fr/it.
      if (typeof n !== 'number') return '';
      return n.toLocaleString(this.locale, { maximumFractionDigits: 4, useGrouping: false });
    },
    _rangeHint(row) {
      const { min, max } = row.cfg;
      const d = this.dict;
      const unit = this._localised(row.cfg.unit);
      const u = unit ? ` ${unit}` : '';
      if (min != null && max != null) return `${d.range} ${d.between} ${this._fmt(min)} ${d.and} ${this._fmt(max)}${u}`;
      if (min != null) return `${d.range} ${d.from} ${this._fmt(min)}${u}`;
      if (max != null) return `${d.range} ${d.upTo} ${this._fmt(max)}${u}`;
      return '';
    },
    placeholderFor(row) {
      const d = row.cfg.decimals;
      return d > 0 ? `0.${'0'.repeat(d)}` : '0';
    },
    evaluationFor(row) {
      return evaluateNumber(this.raw[row.id] || '', row.cfg);
    },
    showErrorFor(row) {
      return !!this.touched[row.id] && this.evaluationFor(row).error != null;
    },
    errorMessageFor(row) {
      const d = this.dict;
      const unit = row.unitText ? ` ${row.unitText}` : '';
      switch (this.evaluationFor(row).error) {
        case 'not_number': return d.errNumber;
        case 'decimals': return `${d.errDecimals} (${row.cfg.decimals} ${d.decimalsNote})`;
        case 'min': return `${d.errMin} ${this._fmt(row.cfg.min)}${unit}.`;
        case 'max': return `${d.errMax} ${this._fmt(row.cfg.max)}${unit}.`;
        default: return '';
      }
    },
    toggleTooltip(rowId) {
      this.tooltipsOpen = { ...this.tooltipsOpen, [rowId]: !this.tooltipsOpen[rowId] };
    },
    onInput(row, e) {
      this.raw = { ...this.raw, [row.id]: e.target.value };
      // Emit every row's parsed value, not just the edited one, so the
      // engine always receives a complete picture of the card.
      const values = {};
      for (const r of this.rows) {
        const text = this.raw[r.id];
        if (typeof text === 'string' && text.trim()) {
          values[r.id] = evaluateNumber(text, r.cfg).value;
        }
      }
      this.$emit('state-changed', { values });
    },
    imageUrl,
    altTextFor,
    onImgError,
  },
};
</script>

<style scoped>
.numeric-grid-card {
  font-family: inherit;
  /* Same tokens as MatrixCard/NumberCard so the three read as one family. */
  --color-bg-card: #ffffff;
  --color-bg-muted: #f1efe8;
  --color-text-primary: #2c2c2a;
  --color-text-secondary: #5f5e5a;
  --color-text-tertiary: #888780;
  --color-border: rgba(0, 0, 0, 0.15);
  --color-accent: #534AB7;
  --color-bad: #C8392E;
  --radius-md: 8px;
}

.section-header { margin-bottom: 0.75rem; }
.section-eyebrow {
  font-size: 11px; font-weight: 600; text-transform: uppercase;
  letter-spacing: 0.06em; color: var(--color-text-tertiary); margin: 0 0 4px;
}
.section-title {
  font-size: 20px; font-weight: 500; margin: 0 0 6px;
  line-height: 1.3; color: var(--color-text-primary);
}
.section-prompt { font-size: 14px; color: var(--color-text-secondary); margin: 0 0 6px; }
.section-progress-text { font-size: 12px; color: var(--color-text-tertiary); margin: 0; }

.stem-image-slot { margin: 0.75rem 0 1rem; }
.stem-image {
  display: block; width: 100%; height: auto;
  border-radius: var(--radius-md); border: 0.5px solid rgba(0, 0, 0, 0.1);
}
.stem-image-placeholder {
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  gap: 6px; height: 140px;
  border: 0.5px dashed rgba(0,0,0,0.18); border-radius: var(--radius-md);
  background: #fafaf7; color: var(--color-text-tertiary); font-size: 12px;
}
.stem-image-icon { font-size: 28px; line-height: 1; }

.config-warning {
  font-size: 13px; color: var(--color-bad); background: #FCEEEC;
  border: 0.5px solid rgba(200, 57, 46, 0.25); border-radius: var(--radius-md);
  padding: 10px 12px; margin: 0.75rem 0;
}

.row-list { margin: 0.25rem 0 0; padding: 0; }

.grid-row { padding: 0.7rem 0; border-bottom: 0.5px solid var(--color-border); }
.grid-row:last-child { border-bottom: none; }

.grid-row-header {
  display: flex; justify-content: space-between; align-items: flex-start;
  gap: 12px; margin-bottom: 6px;
}
.grid-row-label {
  font-size: 14px; font-weight: 500; margin: 0;
  line-height: 1.4; color: var(--color-text-primary); cursor: pointer;
}

.tooltip-button-small {
  background: transparent; border: none; padding: 2px 6px;
  font-size: 14px; color: var(--color-text-tertiary);
  cursor: pointer; flex-shrink: 0; line-height: 1;
}
.tooltip-button-small:hover { color: var(--color-text-secondary); }

.tooltip-content {
  background: var(--color-bg-muted); border-radius: var(--radius-md);
  padding: 8px 10px; font-size: 12px; color: var(--color-text-secondary);
  margin-bottom: 8px; line-height: 1.5;
}

.number-field {
  display: flex; align-items: stretch;
  border: 0.5px solid rgba(0, 0, 0, 0.2);
  border-radius: 10px; background: var(--color-bg-card);
  overflow: hidden; transition: border-color 0.15s, box-shadow 0.15s;
}
.number-field:focus-within {
  border-color: var(--color-accent);
  box-shadow: 0 0 0 3px rgba(83, 74, 183, 0.12);
}
.number-field.is-invalid { border-color: var(--color-bad); }
.number-field.is-invalid:focus-within { box-shadow: 0 0 0 3px rgba(200, 57, 46, 0.12); }

.number-input {
  flex: 1 1 auto; min-width: 0; padding: 11px 14px;
  font-size: 16px; font-family: inherit; color: var(--color-text-primary);
  border: 0; outline: none; background: transparent;
}
.number-input::placeholder { color: #b3b1a8; }

.number-unit {
  display: flex; align-items: center; padding: 0 14px;
  background: var(--color-bg-muted); color: var(--color-text-secondary);
  font-size: 14px; font-weight: 500;
  border-left: 0.5px solid rgba(0, 0, 0, 0.12); white-space: nowrap;
}

.input-footer { margin-top: 5px; min-height: 15px; }
.input-error { font-size: 12px; color: var(--color-bad); margin: 0; }
.input-hint { font-size: 12px; color: var(--color-text-tertiary); }
</style>
